import { SupportWidget } from '@marpple/mco-membership-ui';
import { $, html, type Html, Page, View } from 'rune-ts';
import { ConfigSentryF } from '../../../../../modules/Config/Sentry/F/Function/module/ConfigSentryF';
import { MarketplaceAuthF } from '../../../../../modules/Marketplace/Auth/F/Function/module/MarketplaceAuthF';
import { createOnClickInicisTerms } from '../../../../../modules/MShop/App/Mypage/MembershipPayMethod/component/terms/InicisTerms';
import { createOnClickPersonalInfoOutsourcing } from '../../../../../modules/MShop/App/Mypage/MembershipPayMethod/component/terms/PersonalInfoOutsourcingTerms';
import { createOnClickThirdPartyConsent } from '../../../../../modules/MShop/App/Mypage/MembershipPayMethod/component/terms/ThirdPartyConsentTerms';
import { MShopUtilF } from '../../../../../modules/MShop/Util/F/Function/module/MShopUtilF';
import { BaseLayoutOptions } from '../../../features/Core/service';
import { MEMBERSHIP_UI_IDENTIFIER } from '../../../features/Membership/constant';
import { MembershipErrorEnable } from '../../../features/Membership/error/error_enable';
import { MembershipTypeS, MembershipUtilS } from '../../../features/Membership/outbound/share';
import { getActivatedSdkWithEvent } from '../../../features/Membership/util';
import { MembershipIdType } from '../../../features/ProductList/constant/Membership/tabs';
import { makeEmptyScreenNavigate } from '../../../shared/app/navigate';
import { navigateTo } from '../../../shared/app/util';
import { htmlIf } from '../../../shared/util';
import { autoBind } from '../../../shared/util/autoBind';
import { goLogin } from '../../../shared/util/goLogin';
import { getSharedData } from '../../../shared/util/rune';
import { makePlusUrl } from '../../../shared/util/url';
import { Title } from '../../components/atoms/Title/Title';
import { footer_links } from '../../components/cells/Footer/constant';
import { LayoutView } from '../../components/cells/Layout/LayoutView';
import { SubHeaderMobile } from '../../components/cells/Header/Global/views/SubHeaderMobile.ts';
import { onIntegratedPaidServiceTermsTextClick } from '../PaidServiceTerms/modal';
import klass from './CreatorShopMembership.module.scss';
import { CreatorMainProfile } from '@services/MarppleShop/features/Creator/type';
import type { LocalsStoreMembership } from '@services/MarppleShop/shared/type/locals.ts';
import { TCreatorShopLnbMenu } from '@services/MarppleShop/shared/constant';
import { CreatorShopMainBanner24View } from '@services/MarppleShop/renderApp/components/cells/Header/CreatorShopMainBanner24View.ts';
import { LnbCreatorShop24View } from '@services/MarppleShop/renderApp/components/cells/Header/LnbCreatorShop24View.ts';
import { CreatorConstantS } from '@services/MarppleShop/features/Creator/outbound/share.ts';
import { BestCreatorRanking } from '@services/MarppleShop/features/CreatorRank/type/creator-rank.type.ts';
import { CreatorShopMainBanner24MoView } from '@services/MarppleShop/renderApp/components/cells/Header/CreatorShopMainBanner24MoView.ts';
import { CreatorShopSubHeader } from '@services/MarppleShop/renderApp/components/cells/Header/CreatorShop/views/CreatorShopSubHeader.ts';

export type CreatorShopSupportPageData = {
  type: MembershipIdType;
  layout_options: BaseLayoutOptions;
  membership_project_id: string;
  membership_token?: string;
  domain_name: string;
  plus_domain_name: string;
  support_selected_tab_index?: number;
  creator: CreatorMainProfile;
  adult_check_needed: boolean;
  is_login: boolean;
  membership?: LocalsStoreMembership & {
    my: {
      token?: string;
      memberships?: MembershipTypeS.MembershipSummary[];
    };
  };
  menus: TCreatorShopLnbMenu[];
  best_store_ranking: BestCreatorRanking | undefined;
};

class CreatorShopSupportBodyView extends View<CreatorShopSupportPageData> {
  private _membershipErrorEnable = new MembershipErrorEnable(this);

  protected override template({
    creator,
    adult_check_needed,
    layout_options,
    is_login,
    membership,
    menus,
    best_store_ranking,
  }: CreatorShopSupportPageData): Html {
    const { is_mobile } = this.data.layout_options;
    const sharedData = getSharedData(this);

    const MainBannerView = is_mobile ? CreatorShopMainBanner24MoView : CreatorShopMainBanner24View;

    const LnbView = is_mobile ? CreatorShopSubHeader : LnbCreatorShop24View;

    return html` <div class="${klass.page}">
      ${new MainBannerView({
        cover:
          (creator.is_adult && adult_check_needed) ||
          (is_mobile
            ? creator.banner_img_mo === CreatorConstantS.banner_img
            : creator.banner_img === CreatorConstantS.banner_img)
            ? 'No Cover'
            : 'Default',
        show_adult_creator_thumbnail: Boolean(creator.is_adult && adult_check_needed),
        is_owner: creator.user_id === layout_options.user?.id,
        creator,
        is_login,
        membership,
        best_store_ranking,
      })}
      <div class="${klass.lnb_wrapper}">
        ${new LnbView({
          menus,
          plus_domain: sharedData?.plus_crew?.domain_name,
          focus_on_select: true,
        })}
      </div>

      <div class="${klass.main} ${htmlIf(klass.support, this.data.type === 'support')}">
        ${new Title({
          sub_title: ET('mps2::membership::support::description'),
          size: is_mobile ? 'large' : 'xLarge',
          title: ET('mps2::membership::support::title'),
          custom_klass: klass.title,
        })}
        <div class="${MEMBERSHIP_UI_IDENTIFIER} ${klass.ui}"></div>
      </div>
    </div>`;
  }

  protected override async onRender() {
    const ui_section = $(this.element()).find(`.${MEMBERSHIP_UI_IDENTIFIER}`)?.element();
    try {
      if (ui_section) {
        const client = await getActivatedSdkWithEvent(this.data.membership_token);
        const widget = new SupportWidget({
          client,
          toggleSpinner: MembershipUtilS.toggleSpinner,
          isApp: MShopUtilF.isApp(),
          isMobile: this.data.layout_options.is_mobile,
          projectId: this.data.membership_project_id,
          selectedTabIndex: this.data.support_selected_tab_index ?? 0,
          onGoToExploreProductsButtonClick: () => {
            this._openLink(
              makePlusUrl(this.data.plus_domain_name, `/${ET.lang}/${this.data.domain_name}/products`),
            );
          },
          onSeeSupportDetailButtonClick: (support_id) =>
            this._openLink(
              `/${ET.lang}/@/my_memberships/support_detail/${this.data.membership_project_id}/${support_id}`,
            ),
          onClickMoveToSupportInfoPage: () => {
            location.reload();
          },
          onCreatorSupportButtonClick: this._openSupportPayment,
          onError: this._membershipErrorEnable.onError,
          onIntegratedPaidServiceTermsTextClick,
          onClickPersonalInfoOutsourcing: createOnClickPersonalInfoOutsourcing(),
          onClickThirdPartyConsent: createOnClickThirdPartyConsent(),
          onClickInicisTerms: createOnClickInicisTerms(),
          onScrollLock: MShopUtilF.bodyFixed$,
        });
        widget.render(ui_section);
      }
    } catch (e) {
      ConfigSentryF.error(e);
      await this._membershipErrorEnable.openWidgetRenderErrorModal();
    }
  }

  private _openLink(link: string) {
    navigateTo(link, makeEmptyScreenNavigate);
  }

  @autoBind()
  private _openSupportPayment(open: () => void) {
    if (!MarketplaceAuthF.isSignedIn()) {
      return goLogin();
    }

    return open();
  }
}

export class CreatorShopSupportPage extends Page<CreatorShopSupportPageData> {
  protected override template(): Html {
    const sharedData = getSharedData(this);
    const { layout_options } = this.data;

    return html`
      <div>
        ${new LayoutView(
          {
            body: new CreatorShopSupportBodyView(this.data),
            footer_data: { ...footer_links, is_mobile: layout_options.is_mobile, lang: ET.lang },
          },
          {
            full_width: true,
            has_bottom_tab_bar: !sharedData?.webviewapp,
            selected_bottom_tab_idx: -1,
            ...layout_options,
          },
        )}
      </div>
    `;
  }

  override onMount() {
    super.onMount();
    SubHeaderMobile.handleChangeMenuOutside();
  }

  static getSupportPageTabIndex(tab_name: 'info' | 'message') {
    return {
      // SUPPORT의 후원 안내 탭
      info: 0,

      // SUPPORT의 후원 메세지 탭
      message: 1,
    }[tab_name];
  }
}
